import qs from "qs"
import { ADMIN_ENDPOINTS, ORCHESTRATOR_ENDPOINTS } from "common/endpoints";
import { request } from "common/services/request";
import { MemoryFact } from "common/services/stream";

export type GuidelineDocReference = {
  document_id: string;
  image_url: string | null;
};

export type Message = {
  message_id: string;
  conversation_id: string;
  role: string;
  kind: string;
  content: string;
  guideline_chunks?: string[] | null;
  guideline_docs?: GuidelineDocReference[] | null;
  // Recalled cross-conversation memory facts + provenance for this answer.
  memory_facts?: MemoryFact[] | null;
  reply_to_id?: string | null;
  created_at: string;
  updated_at: string;
};

type CreateMessagePayload = {
  conversation_id: string;
  role: string;
  content: string;
  kind?: string;
  reply_to_id?: string;
  guideline_chunks?: string[];
  guideline_docs?: Array<{
    document_id: string;
    image_url: string | null;
  }>;
};

export const createMessage = (payload: CreateMessagePayload) => {
  return request("orchestrator").post(
    ORCHESTRATOR_ENDPOINTS.MESSAGES.BASE,
    payload
  );
};

export const listMessages = (conversationId: string, limit = 500, offset = 0) => {
  return request("orchestrator").get<Message[]>(
    ORCHESTRATOR_ENDPOINTS.MESSAGES.BASE,
    {
      params: {
        conversation_id: conversationId,
        limit,
        offset,
      },
    }
  );
};

// Resolve a conversation's shortCode (used for routing) from its UUID — the
// memory-fact sources carry the conversation_id, but the chat route is /c/:shortcode.
export const getConversationShortCode = async (conversationId: string): Promise<string | null> => {
  try {
    const res = await request("orchestrator").get<{ short_code?: string }>(
      ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.BY_ID(conversationId)
    );
    return res.data?.short_code ?? null;
  } catch {
    return null;
  }
};

export const getGuidelineChunk = (chunkId: string) => {
  return request("orchestrator").get(
    ADMIN_ENDPOINTS.GUIDELINES.CHUNK_BY_ID(chunkId)
  );
};

export const getGuidelineDoc = (docId: string) => {
  return request("orchestrator").get(
    ADMIN_ENDPOINTS.GUIDELINES.DOC_BY_ID(docId)
  );
};

export type GuidelineChunkReferenced = {
  guideline_chunk_id: string;
  raw_text: string | null;
};

export type GuidelineSectionDisplay = {
  guideline_section_id: string;
  raw_text: string | null;
  page_no: number;
  keywords: string[];
  chunks_referenced: GuidelineChunkReferenced[];
};

export type GuidelineDocDisplay = {
  document: {
    guideline_docs_id: string;
    title: string;
    description: string;
    image_url: string | null;
    source_org: string;
    author: string;
    version_label: string;
    published_date: string;
    url: string;
    language: string;
    jurisdiction: string;
  };
  section: GuidelineSectionDisplay[];
};

export const getGuidelineDisplay = (
  documentIds: string[],
  guidelineChunkIds: string[],
) => {
  return request("orchestrator").get<GuidelineDocDisplay[]>(
    ADMIN_ENDPOINTS.GUIDELINES.DISPLAY,
    {
      params: { document_ids: documentIds, guideline_chunk_ids: guidelineChunkIds },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
    },
  );
};

